import React from 'react';

import 'bootstrap/dist/css/bootstrap.min.css';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import translationsJson from 'data/translations.json';

import WordCloud from 'components/Wordcloud/Wordcloud';
import MapContainer from 'components/Map/MapContainer';
import lastnameIndices from 'data/lastnameIndex.json';
import Ranking from 'components/Ranking/Ranking';
import rankingNamesJson from 'data/lastnameRanking.json';
import lastnameMunicipalityRankingJson from 'data/lastnameMunicipalityRanking.json';
import municipalitiesJson from "data/municipality.json";

import { NameType, WordCloudEntry, Sex, LastnameRankingData, LanguageGroup, DownloadType, Municipalities } from 'lib/Types';

import info from 'data/info.json';
import 'general.css';
import LanguageFilter from 'components/LanguageFilter/LanguageFilter';
import MunicipalityFilter from 'components/MunicipalityFilter/MunicipalityFilter';
import Annotation from 'components/Annotation/Annotation';
import DownloadSection from 'components/DownloadSection/DownloadSection';
import Indication from 'components/Indication/Indication';
import { MobileDeviceContext } from 'lib/Screen';
import withRouter from 'lib/withRouter';
import { NavigateFunction } from 'react-router-dom';

const municipalities: Municipalities = municipalitiesJson;

const translations: Translations = translationsJson;
const rankingNames: LastnameRankingData = rankingNamesJson;
const lastnameMunicipalityRanking: LastnameRankingData = lastnameMunicipalityRankingJson;

enum TabNames {
    Wordcloud = 0,
    Ranking = 1
}

interface Props { 
	params: Record<string, string>;
	navigate: NavigateFunction;
}

interface State { 
    tabKey: string;
    selectedName: string | undefined;
    language: LanguageGroup[];
    municipality: string;
}

class Lastnames extends React.Component<Props, State> {
    static contextType = MobileDeviceContext;

    private wordCloudNames: WordCloudEntry[];

    constructor(props: Props) {
        super(props);

        this.state = {
            tabKey: 'tab' in this.props.params ? this.props.params['tab'] : "wordcloud",
            selectedName: undefined,
            language: [LanguageGroup.All, LanguageGroup.All],
            municipality: Object.keys(municipalities)[0],
        };


        this.wordCloudNames = [];
        this.generateWordCloudNames(this.state.language[TabNames.Wordcloud]);
        this.onNameSelected = this.onNameSelected.bind(this);
        this.onMunicipalitySelected = this.onMunicipalitySelected.bind(this);
        this.onUpdateLanguage = this.onUpdateLanguage.bind(this);
    }

    private onNameSelected(name: string, sex?: Sex) {
        this.setState({ tabKey: "map", selectedName: name });
        this.props.navigate("map");
    }

    private onMunicipalitySelected(municipality: string) {
        this.setState({ tabKey: "rankingMunicipality", municipality: municipality });
        this.props.navigate("rankingMunicipality");
    }

    private onUpdateLanguage(newLanguage: LanguageGroup, index: number) {
        if (index === TabNames.Wordcloud) {
            this.generateWordCloudNames(newLanguage);
        }
        const language = this.state.language;
        language[index] = newLanguage;
        this.setState({ language: language });
    }

    private onUpdateSelectedMunicipality(municipality: string) {
        this.setState({ municipality: municipality });
    }

    private generateWordCloudNames(language: LanguageGroup) {
        const rankingData = rankingNames[language];
        this.wordCloudNames = rankingData.slice(0, Math.min(rankingData.length, info.wordCloudNameCount)).map(item => ({ text: item.name, value: item.count }));
    }

    private renderWordCloud() {
        return (
            <>
                <div className="filter-div">
                    <LanguageFilter onUpdate={(e) => { this.onUpdateLanguage(e, TabNames.Wordcloud) }} />
                </div>
                <WordCloud words={this.wordCloudNames} nameSelected={this.onNameSelected} />
            </>
        );
    }

    private renderMap() {
        return (
            <MapContainer nameIndices={lastnameIndices} nameType={NameType.Lastname} selectedPerson={this.state.selectedName ? { name: this.state.selectedName } : undefined} onClick={this.onMunicipalitySelected} />
        );
    }

    private renderRanking() {
        const rankingData = rankingNames[this.state.language[TabNames.Ranking]].slice(0, Math.min(rankingNames[this.state.language[TabNames.Ranking]].length, info.lastnameRankingCount));

        return (
            <>
                <div className="filter-div">
                    <LanguageFilter onUpdate={(e) => { this.onUpdateLanguage(e, TabNames.Ranking) }} />
                </div>
                <div>
                    <Ranking names={rankingData} title={translations.lastnames[window.lang] + " - " + `${info.lastnamesYear}`} nameSelected={this.onNameSelected}></Ranking>
                </div>
            </>
        );
    }

    private renderRankingMunicipality() {
        return (
            <>
                <div className="filter-div">
                    <MunicipalityFilter value={this.state.municipality} onUpdate={(e) => { this.onUpdateSelectedMunicipality(e) }} />
                </div>
                <div>
                    <Ranking names={lastnameMunicipalityRanking[this.state.municipality]} title={municipalities[this.state.municipality].name[window.lang] + " - " + info.lastnamesYear} nameSelected={this.onNameSelected}></Ranking>
                </div>
            </>
        );
    }

    componentDidUpdate(prevProps: Props) {
        const prevTab = 'tab' in prevProps.params ? prevProps.params['tab'] : null;
        const tab = 'tab' in this.props.params ? this.props.params['tab'] : null;
        if (tab && prevTab !== tab)
            this.setState({ tabKey: tab })
    }

    render() {
        let content: JSX.Element;
        //console.log("lastname params:", this.props.params);
        if (this.context) {
            switch (this.state.tabKey) {
                case 'wordcloud':
                    content = this.renderWordCloud();
                    break;
                case 'map':
                    content = this.renderMap();
                    break;
                case 'ranking':
                    content = this.renderRanking();
                    break;
                case 'rankingMunicipality':
                    content = this.renderRankingMunicipality();
                    break;
                default:
                    content = <h3>404 Not Found</h3>
                    break;
            }


        } else {
            content = (
                <Tabs defaultActiveKey="wordcloud" activeKey={this.state.tabKey} onSelect={(key) => this.setState({ tabKey: key! })} className="mb-3">
                    <Tab eventKey="wordcloud" title={translations.wordcloud[window.lang]}>
                        {this.renderWordCloud()}
                    </Tab>
                    <Tab eventKey="map" title={translations.map[window.lang]}>
                        {this.renderMap()}
                    </Tab>
                    <Tab eventKey="ranking" title={translations.ranking[window.lang]}>
                        {this.renderRanking()}
                    </Tab>
                    <Tab eventKey="rankingMunicipality" title={translations.rankingMunicipality[window.lang]}>
                        {this.renderRankingMunicipality()}
                    </Tab>
                </Tabs>
            );
        }

        return (
            <>
                {content}

                <Annotation page='lastnames' tab={this.state.tabKey} />
                <DownloadSection type={DownloadType.Lastnames} />
                <Indication />
            </>
        );
    }
}

export default withRouter(Lastnames);